import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { withStyles } from "@material-ui/core/styles";
import { WithStyles } from "@material-ui/core";
import { styles } from "./ManageUseCasesView.styles";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import * as UseCasesAction from "../../../../stores/useCases/UseCasesAction";
import ToastStatusEnum from "../../../../constants/ToastStatusEnum";
import { IUniversalTableColumn, IUniversalTableData } from "../../../components/UniversalTable/interfaces";
import UseCaseCategoryModel from "../../../../models/useCase/UseCaseCategoryModel";
import UseCaseModel from "../../../../models/useCase/UseCaseModel";
import UniversalTable from "../../../components/UniversalTable/UniversalTable";
import { findProductByName } from "../../productOverview/ProductHelper";

export interface IManageUseCasesCategoriesViewProps extends WithStyles<typeof styles> {}

const ManageUseCasesCategoriesViewPure: React.FunctionComponent<IManageUseCasesCategoriesViewProps> = ({
    classes
}: IManageUseCasesCategoriesViewProps) => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();

    const productsFromState = useSelector(state => state.products);
    const categoriesFromState = useSelector(state => state.useCases.categories);
    const useCasesFromState = useSelector(state => state.useCases.useCases);

    let useCases: UseCaseModel[];
    if (useCasesFromState) {
        useCases = Object.values(useCasesFromState);
    }

    let categories: IUniversalTableData;
    if (categoriesFromState) {
        categories = Object.values(categoriesFromState)?.map((category: UseCaseCategoryModel) => {
            const hasUseCases = useCases?.find(uc => uc.categories?.includes(category.id)) !== undefined;
            return {
                id: category.id,
                name: category.name,
                products: category.products?.map(p => ({
                    id: p,
                    label: findProductByName(productsFromState?.products, p)?.name
                })),
                ...(hasUseCases && {
                    actionsDisabled: ["delete"]
                })
            };
        });
    }

    const handleEditFinished = (index, newData) => {
        const updatedCategory = {
            name: newData.name,
            products: newData?.products?.map(p => p.id)
        };
        dispatch(UseCasesAction.updateCategory(categories[index].id, updatedCategory))
            .then(() => {
                dispatch(UseCasesAction.requestAllCategories());
                enqueueSnackbar("Use case category updated", {
                    variant: ToastStatusEnum.Info
                });
            })
            .catch(error =>
                enqueueSnackbar("Use case category update failed: " + error, {
                    variant: ToastStatusEnum.Error
                })
            );
    };

    const handleDeleteFinished = index => {
        dispatch(UseCasesAction.deleteCategory(categories[index].id))
            .then(() => {
                dispatch(UseCasesAction.requestAllCategories());
                enqueueSnackbar("Use case category deleted", {
                    variant: ToastStatusEnum.Info
                });
            })
            .catch(error =>
                enqueueSnackbar("Use case category deletion failed: " + error, {
                    variant: ToastStatusEnum.Error
                })
            );
    };

    const handleAddFinished = data => {
        const newCategory = {
            name: data.name,
            products: data?.products?.map(p => p.id)
        };
        dispatch(UseCasesAction.addCategory(newCategory))
            .then(() => {
                dispatch(UseCasesAction.requestAllCategories());
                enqueueSnackbar("Use case category added", {
                    variant: ToastStatusEnum.Info
                });
            })
            .catch(error =>
                enqueueSnackbar("Use case category addition failed: " + error, {
                    variant: ToastStatusEnum.Error
                })
            );
    };

    const columns: IUniversalTableColumn[] = [
        {
            type: "multiline_text",
            noLines: 3,
            title: "Category name",
            field: "name",
            validate: rowData => {
                return rowData?.name?.length > 0 ? true : { isValid: false, helperText: "Name cannot be empty" };
            }
        },
        {
            type: "multiselect",
            title: "Products",
            field: "products",
            allValues: productsFromState.products?.map(p => ({ id: p.id, label: p.name }))
        },
        {
            type: "actions",
            title: t("admin.taskView.actions"),
            actions: [
                {
                    name: "edit",
                    tooltip: t("general.edit"),
                    onEditFinished: (index, newData) => handleEditFinished(index, newData),
                    scope: "usecases.write"
                },
                {
                    name: "delete",
                    tooltip: t("general.delete"),
                    onDeleteFinished: index => handleDeleteFinished(index),
                    deleteQuestion: "Do you want to delete this category?",
                    scope: "usecases.write",
                    disabledTooltip: "Please delete/unnasign assigned use cases first."
                },
                {
                    name: "add",
                    tooltip: "Add category",
                    onAddFinished: newData => handleAddFinished(newData),
                    scope: "usecases.write"
                }
            ]
        }
    ];

    return (
        <div>
            <UniversalTable
                columns={columns}
                data={categories}
                searchable
            />
        </div>
    );
};

export const ManageUseCasesCategoriesView = withStyles(styles)(ManageUseCasesCategoriesViewPure);
